import {
  gerIdcByRegion,
  getCompassCityId,
  getCompassRegion,
  SUPPORT_REGIONS,
  setCompassCityId,
  setCompassRegion,
} from '@sailor/compass';
import { isRtlLocale } from '@sailor/i18n-rtl';
import { getCacheLocale, i18nClient, MCity, } from '@sailor/i18n-web';
import axios from 'axios';
import qs from 'qs';
import {
  AWPENVMAP,
  cHosts,
  clientEnvHostConfig,
  DEFAULT_CITY_ID,
  DEFAULT_REGION,
  HOSTENVMAP,
  passportApiHosts,
  passportSwitchAuthApiHosts,
  passportConfig,
  passportService,
  merchantCashierRegionHosts,
  paymentHosts,
} from '../constants';

import {
  getFristQueryValue, isInApp, safeParse,
} from './common';

const ua = typeof navigator === 'undefined'
  ? ''
  : navigator.userAgent || '';

export const isHarmonyOS = ua.includes('HarmonyOS');

const isArkWeb = ua.includes('ArkWeb') || ua.includes('ArkWebView');

export const isHarmonyArkWeb = isHarmonyOS && isArkWeb;

/**
 * 获取浏览器系统语言,默认值(en)
 */
export const getBrowserSystemLanguage = (): string => {
  try {
    return navigator.language || (navigator as any).userLanguage || 'en';
  } catch (e) {
    console.warn('Failed to get browser system language:', e);
    return 'en';
  }
};

export const normalizeTrimmedString = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * i18n 初始化前按 language detector 的优先级解析语言。
 */
export const getInitialLanguage = (): string => {
  try {
    const queryLocale = normalizeTrimmedString(getFristQueryValue('locale'));
    if (queryLocale) return queryLocale;
  } catch (e) {
    console.log(e);
  }

  try {
    const sessionLocale = normalizeTrimmedString(sessionStorage.getItem('i18nLocale'));
    if (sessionLocale) return sessionLocale;
  } catch (e) {
    console.log(e);
  }

  try {
    return normalizeTrimmedString(getCacheLocale()) || 'en';
  } catch (e) {
    console.log(e);
    return 'en';
  }
};

export const getLanguage = (): string => {
  try {
    const currentLocale = i18nClient.isInitialized
      ? normalizeTrimmedString(i18nClient.getCurrentLocale())
      : '';
    return currentLocale || getInitialLanguage();
  } catch (e) {
    console.log(e);
    return getInitialLanguage();
  }
};

// 页面嵌入D端时使用
export const isFromD = () => {
  const { host } = window.top ? window.top.location : window.location;
  // 增加本地调试环境变量
  try {
    // let process;
    return ENV_CLIENT_TYPE === 'd_pc' || host.indexOf('courier.mykeeta') !== -1;
  } catch (e) {
    return host.indexOf('courier.mykeeta') !== -1;
  }
};

// 页面嵌入M端时使用
export const isFromM = () => {
  const { host } = window.top ? window.top.location : window.location;
  try {
    // let process;
    return ENV_CLIENT_TYPE === 'm_pc' || host.indexOf('opcenter.mykeeta') !== -1;
  } catch (e) {
    return host.indexOf('opcenter.mykeeta') !== -1;
  }
};

const normalizeSupportedRegion = (value: unknown) => {
  const region = value === undefined || value === null
    ? ''
    : String(value).trim().toUpperCase();
  return SUPPORT_REGIONS.includes(region) ? region : '';
};

const normalizeRuntimeCityId = (value: unknown) => {
  const cityId = Number(value);
  return Number.isSafeInteger(cityId) && cityId > 0 ? cityId : undefined;
};

const getRuntimeDefaultRegion = () => {
  try {
    const initialRegion = normalizeSupportedRegion(window.__initRegion);
    if (initialRegion) return initialRegion;
  } catch (e) {
    console.log(e);
  }
  return normalizeSupportedRegion(DEFAULT_REGION);
};

const getRuntimeDefaultCityId = () => {
  try {
    const initialCityId = normalizeRuntimeCityId(window.__initCityId);
    if (initialCityId) return initialCityId;
  } catch (e) {
    console.log(e);
  }
  return DEFAULT_CITY_ID;
};

const getRegionPacks = () => {
  const packs = localStorage.getItem('i18nLocale_m');
  if (packs) {
    return normalizeSupportedRegion(safeParse(packs)?.curRegion?.code);
  }
  return '';
};

const COMPASS_STATE_READ_OPTIONS = {
  storageMode: 'persistent',
} as const;

const COMPASS_STATE_WRITE_OPTIONS = {
  storageMode: 'persistent',
  syncUrl: 'if-present',
} as const;

export const setCityId = (id: number) => {
  const cityId = normalizeRuntimeCityId(id);
  if (!cityId) return undefined;
  return setCompassCityId(cityId, COMPASS_STATE_WRITE_OPTIONS);
};

export function getCityId() {
  const defaultCityId = getRuntimeDefaultCityId();
  let cityId: string | number = getCompassCityId('', COMPASS_STATE_READ_OPTIONS);
  try {
    if (!cityId && isFromM()) {
      cityId = MCity.getCapitalCityIdByRegion(getRegionPacks()) || defaultCityId;
    }
  } catch (e) {
    console.log(e);
  }
  return normalizeRuntimeCityId(cityId) || defaultCityId;
}

export const setRegion = (region: string) => {
  const supportedRegion = normalizeSupportedRegion(region);
  if (!supportedRegion) return undefined;
  return setCompassRegion(supportedRegion, COMPASS_STATE_WRITE_OPTIONS);
};

export function getRegion() {
  const defaultRegion = getRuntimeDefaultRegion();
  let region = normalizeSupportedRegion(getCompassRegion('', COMPASS_STATE_READ_OPTIONS));
  try {
    if (!region && isFromM()) {
      region = getRegionPacks() || defaultRegion;
    }
  } catch (e) {
    console.log(e);
  }
  return region || defaultRegion;
}

export function getTimeZone() {
  try {
    // 方法1: 使用 Intl.DateTimeFormat (现代浏览器)
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      const date = new Date();
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // @ts-ignore
          timeZoneName: 'longOffset' // ts版本太老，字段定义不准确
        });

        const formattedDate = formatter?.format(date) || '';
        if (formattedDate.includes('GMT')) {
          const timeZoneOffset = (formattedDate.split(' ').pop() || '')
            .replace('GMT', '')
            .replace(/(\d{2})(\d{2})/, '$1:$2');

          if (timeZoneOffset) return `GMT${timeZoneOffset}`;
        }
      } catch (e) {
        console.warn('Error using Intl.DateTimeFormat for timezone:', e);
        // 继续使用备用方法
      }
    }

    // 方法2: 使用 Date 对象 (更广泛兼容)
    const offset = new Date().getTimezoneOffset();
    const hours = Math.abs(Math.floor(offset / 60));
    const minutes = Math.abs(offset % 60);
    const sign = offset > 0 ? '-' : '+';

    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (e) {
    console.error('Failed to get timezone:', e);
    return 'GMT+08:00'; // 默认返回
  }
}

// c_h5_for_bd
export const getI18nParams = () => ({
  // clientType: getClientType(),
  locale: getLanguage(),
  region: getRegion(),
  cityId: getCityId(),
  timeZone: getTimeZone(),
});

export const setLoginShopid = (id) => {
  let shopid = `${id}`;
  if (id === undefined) {
    shopid = '-1';
  }
  sessionStorage.setItem('LOGIN_SHOPID', shopid);
};

const getLoginShopInfo = () => {
  const infoJson = sessionStorage.getItem('LOGIN_SHOPINFO') || '{}';
  return safeParse(infoJson);
};

const _validateShopId = (shopId, ignoreNumber?) => {
  const numberShopId = +shopId;
  return Number.isNaN(numberShopId) || (!ignoreNumber && (numberShopId === 0 || numberShopId === -1));
};

export function machLoadingChange(loading: boolean) {
}

export const isRTL = (locale = getLanguage()) => {
  try {
    return isRtlLocale(locale);
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const sortStringByRTL = (list: string | string[], ...params: string[]) => {
  if (Array.isArray(list)) {
    return isRTL() ? list.reverse().join('') : list.join('');
  }
  const _list = [list, ...params];
  return isRTL() ? _list.reverse().join('') : _list.join('');
};

export const keyBoardDismissHandle = () => {
};

export const forceLTR = (str) => str;

export const getLoginUinfo = () => {
  const infoJson = sessionStorage.getItem('LOGIN_UINFO') || '{}';
  return safeParse(infoJson) || {};
};

export const getLoginUid = () => {
  const infoJson = sessionStorage.getItem('LOGIN_ACCOUNTID') || '0';
  return safeParse(infoJson, '0');
};

export const setLoginUinfo = (info) => {
  const oldAccountInfo = getLoginUinfo();
  const accountInfo = { ...oldAccountInfo, ...(info || {}) };
  sessionStorage.setItem('LOGIN_UINFO', JSON.stringify(accountInfo));
};

export const clearLoginUinfo = () => {
  sessionStorage.removeItem('LOGIN_UINFO');
};

export const isLocal = (ihost?) => {
  const host = ihost || (window.top ? window.top.location : window.location).host;
  if (/^localhost/.test(host)) return true;
  if (HOSTENVMAP[host]) return false; // 已知部署域名优先，避免被 test 环境变量误判为本地
  if (!process_env_AWP_DEPLOY_ENV || process_env_AWP_DEPLOY_ENV === AWPENVMAP.local) return true;
  return false;
};

// 获取当前域名
// 注意：local会返回test的host
const getNaviHost = (hostOnly = false) => {
  const { host, protocol } = window.top ? window.top.location : window.location;
  const currentHost = isLocal(host) ? cHosts.test : host;
  return hostOnly ? currentHost : `${protocol}//${currentHost}`;
};

const getEnvHost = (hostOnly = false) => {
  const { host, protocol } = window.top ? window.top.location : window.location;
  const currentHost = isLocal(host) ? cHosts.test : host;
  return hostOnly ? currentHost : `${protocol}//${currentHost}`;
};

// return test st prod。 ！！！local返回test
export const getEnv = (processEnv?: { AWP_DEPLOY_ENV: any; }, host?: string) => {
  if (isLocal()) return 'test';
  try {
    if (process_env_AWP_DEPLOY_ENV || processEnv?.AWP_DEPLOY_ENV) {
      const AWP_DEPLOY_ENV = process_env_AWP_DEPLOY_ENV || processEnv?.AWP_DEPLOY_ENV;
      return AWPENVMAP[AWP_DEPLOY_ENV] || 'prod';
    }
    return HOSTENVMAP[host || getNaviHost(true)] || 'prod';
  } catch (e) {
    console.log(e);
    return HOSTENVMAP[host || getNaviHost(true)] || 'prod';
  }
};

export const isTestEnv = () => {
  const env = getEnv();
  return !['prod', 'st'].includes(env);
};

// passport登录地址
export const getPassportLoginUrl = (url?, lng?) => {
  const {
    origin, href, host, protocol, pathname, search, hash
  } = window.top ? window.top.location : window.location;
  // 登录前URL路径
  const loginBackOrigin = isLocal(host) ? `${protocol}//${cHosts.test}` : origin;
  // const loginBackOrigin = origin // backUrl 必须在passport的白名单
  const env = getEnv();
  const passportConf = passportConfig[env];
  const { host: configuredPassportHost, tokenId, joinkey } = passportConf;
  const passportHost = env === 'prod' && host === 'fooddelivery-eu.mykeeta.com'
    ? 'passport.mykeeta.com'
    : configuredPassportHost;
  const language = lng || getLanguage(); // 兜底:en
  const region = getRegion(); // 兜底:HK
  const cityId = getCityId(); // 兜底:810001
  const searchObj = qs.parse(search, { ignoreQueryPrefix: true });
  searchObj.locale = language;
  const backUrl = encodeURIComponent(`${loginBackOrigin}${url || pathname}?${qs.stringify(searchObj)}${hash}`);
  return `${protocol}//${passportHost}/pc/login?service=${passportService}&locale=${language}&region=${region}&cityId=${cityId}&token_id=${tokenId}&joinkey=${joinkey}&protocolType=checkbox&gray=1&backurl=${backUrl}`;
};

// 跳passoort登录
export const toLogin = () => {
  const passportLoginUrl = getPassportLoginUrl();
  window.top ? (window.top.location.href = passportLoginUrl) : (location.href = passportLoginUrl);
};

// 获取H5链接泳道前缀（拼接泳道域名场景需要给下发的链接上拼入）
export const getSwimlanePrefix = () => {
  try {
    const ss = process_env_SWIMLANE || '';
    return ss ? `${ss}-sl-` : '';
  } catch (e) {
    console.log(e);
    return '';
  }
};

export const getPaymentUrl = (needProtocol?, needSwimlane?) => {
  const host = paymentHosts[getEnv()];
  if (!needProtocol && !needSwimlane) return host;
  const protocol = window.location.protocol;
  const swimlane = getSwimlanePrefix();
  return `${needProtocol ? `${protocol}//` : ''}${needSwimlane ? swimlane : ''}${host}`;
};

export const getMerchantCashierUrl = (needProtocol?) => {
  // Saudi Arabia uses the Hong Kong IDC; all other Merchant Cashier regions use EU IDC.
  const idc = getRegion() === 'SA' ? 'hk' : 'eu';
  const host = merchantCashierRegionHosts[idc][getEnv()];
  if (!needProtocol) return host;
  return `${window.location.protocol}//${host}`;
};

// client: cs/payment/passport/c/b/m
export const getHostUrl = (options) => {
  const { client, needProtocol, needSwimlane } = options || {};
  const host = (clientEnvHostConfig[client] || cHosts)[getEnv()];
  if (!needProtocol && !needSwimlane) return host;
  const protocol = window.location.protocol;
  const swimlane = getSwimlanePrefix();
  return `${needProtocol ? `${protocol}//` : ''}${needSwimlane ? swimlane : ''}${host}`;
};

// passport api接口域名
export const getPassportApiHost = () => {
  const curHost = getNaviHost(true); // 当前域名
  const curRegion = getRegion();
  const curIDC = gerIdcByRegion(curRegion); // 不要使用process_env_publishIdc环境变量判断，不是当前代码部署的机房，是当前region所在的机房
  const curEnvpassportApiHosts = passportApiHosts[getEnv()]; // 当前环境passportApi的域名
  const curIDCPassportApiHost = curEnvpassportApiHosts[curIDC];

  if (!curHost) return curIDCPassportApiHost?.[0];
  // 获取当前页面二级域名一致的passportApi域名
  const curPageSubLevelDomain = curHost.split('.').slice(-2).join('.');
  const curPagePassportApiHost = curIDCPassportApiHost.find((host) => host.includes(curPageSubLevelDomain));

  return curPagePassportApiHost ?? curIDCPassportApiHost?.[0];
};

// passport 切换token接口域名
export const getOpenapiPassportApiHost = (sourceRegion?: string) => {
  const curHost = getNaviHost(true); // 当前域名
  const curRegion = sourceRegion || getRegion();
  const curIDC = gerIdcByRegion(curRegion); // 不要使用 process_env_publishIdc 环境变量判断，不是当前代码部署的机房，是当前region所在的机房
  const curEnvOpenapiPassportApiHosts = passportSwitchAuthApiHosts[getEnv()]; // 当前环境 openapiPassportApi 的域名
  const curIDCOpenapiPassportApiHost = curEnvOpenapiPassportApiHosts[curIDC];

  if (!curHost) return curIDCOpenapiPassportApiHost?.[0];
  // 获取当前页面二级域名一致的passportApi域名
  const curPageSubLevelDomain = curHost.split('.').slice(-2)
    .join('.');
  const curPagePassportApiHost = curIDCOpenapiPassportApiHost.find((host) => host.includes(curPageSubLevelDomain));

  return curPagePassportApiHost ?? curIDCOpenapiPassportApiHost?.[0];
};

// http://passport-i18n.fe.test.sankuai.com/pc/person-info?joinkey=1101416_49114003&token_id=Hgujr3Nk87Ps3lFEOkZV2Q&service=merchants
export const getPassportUinfoUrl = (lng?) => {
  const { origin, host, protocol } = window.top ? window.top.location : window.location;
  const passportConf = passportConfig[getEnv()];
  const { host: passportHost, tokenId, joinkey } = passportConf;
  const language = lng || getLanguage(); // 兜底:en
  const region = getRegion(); // 兜底:HK
  const cityId = getCityId(); // 兜底:810001
  return `${protocol}//${passportHost}/pc/person-info?service=${passportService}&locale=${language}&region=${region}&cityId=${cityId}&token_id=${tokenId}&joinkey=${joinkey}&banner=false&showBackButton=true`;
};

// Passport 区号选择页地址
export const getPassportCountryCodeUrl = (backUrl: string) => {
  const { protocol } = window.location;
  const passportConf = passportConfig[getEnv()];
  const passportHost = passportConf?.host;
  if (!passportHost || !backUrl) return '';

  const query = new URLSearchParams({
    locale: getLanguage(),
    region: getRegion(),
    service: passportService,
    backUrl,
  });
  return `${protocol}//${passportHost}/i/country-code?${query.toString()}`;
};

// Mine 页面跳转 Passport 个人信息页地址
export const getMinePassportUinfoUrl = (channel?, lng?) => {
  const { protocol } = window.top ? window.top.location : window.location;
  const passportConf = passportConfig[getEnv()];
  const { host: passportHost, tokenId, joinkey } = passportConf;
  const language = lng || getLanguage();
  const region = getRegion();
  const cityId = getCityId();
  const fromSource = channel === 'googlemap' ? 'web-gmap' : 'web-harmony';
  return `${protocol}//${passportHost}/pc/person-info?locale=${language}&region=${region}&cityId=${cityId}&risk_cost_id=&joinkey=${joinkey}&token_id=${tokenId}&isShowLogout=true&from_source=${fromSource}&banner=false&showBackButton=true`;
};

// https://km.sankuai.com/collabpage/1463829017
// 必须cookie里有token字段，否则接口报错，无法正确跳转登录页
export const getPassportLogoutUrl = (url?, lng?) => {
  const { protocol } = window.top ? window.top.location : window.location;
  const passportConf = passportConfig[getEnv()] || {};
  const { joinkey } = passportConf;
  const language = lng || getLanguage(); // 兜底:en
  const region = getRegion(); // 兜底:HK
  const cityId = getCityId(); // 兜底:810001
  return `${protocol}//${
    getPassportApiHost()
  }/api/user/v1/logout/pc/reset-token?type=10000&redirect_url=${
    encodeURIComponent(getPassportLoginUrl(url, language))
  }&locale=${language}&region=${region}&cityId=${cityId}&joinkey=${joinkey}`;
};

// 退登。成功失败回调、重新登录url,登录页语言
export const logout = ({ success, fail }, url?, lng?) => {
  const logoutUrl = getPassportLogoutUrl(url, lng);
  const request = axios.create({ withCredentials: true });
  request.get(logoutUrl, { withCredentials: true }).then((res) => {
    // owlAddJsInfo('logout error', res) // 无token请求接口会返回error code 400
    console.log('logout success', res);
    // 清空缓存的用户数据
    clearLoginUinfo();
  })
    .catch((e) => {
      console.log(e); // cookie token正常清除，会302走到catch
      fail?.();
    })
    .finally(() => {
      // cleanLoginInfo()
      toLogin();
      success?.();
    });
};

// 只退登，不跳转到登录页
export const logoutOnly = ({ success, fail }, url?, lng?) => {
  const logoutUrl = getPassportLogoutUrl(url, lng);
  const request = axios.create({ withCredentials: true });
  request.get(logoutUrl, { withCredentials: true }).then((res) => {
    // owlAddJsInfo('logout error', res) // 无token请求接口会返回error code 400
    console.log('logout success', res);
  })
    .catch((e) => {
      console.log(e); // cookie token正常清除，会302走到catch
      fail?.();
    }).finally(() => {
    // 清空缓存的用户数据
      clearLoginUinfo();
      success?.();
    });
};

// 获取客户端类型, App：BD-app, Bpartner-app, Bpos, PC：bpc,dpc,mpc
// TODO：完善类型逻辑，确认默认兜底值
export const getClientType = () =>
  // eslint-disable-next-line no-nested-ternary
  ENV_CLIENT_TYPE || getFristQueryValue('clientType') || (isFromM() ? (isInApp() ? 'bd' : 'c_h5_m') : 'c_h5');

export function arabicToStandardNumbers(input: string) {
  const arabicNumbers = '٠١٢٣٤٥٦٧٨٩'; // 东阿拉伯数字
  const standardNumbers = '0123456789'; // 标准阿拉伯数字

  return input
    .split('') // 将字符串拆分为字符数组
    .map((char) => {
      const index = arabicNumbers.indexOf(char);
      return index !== -1 ? standardNumbers[index] : char; // 替换为标准数字或保留原字符
    })
    .join(''); // 将字符数组重新拼接为字符串
}

export function containsArabicNumbers(input: string) {
  const arabicNumberPattern = /[٠١٢٣٤٥٦٧٨٩]/;

  return arabicNumberPattern.test(input);
}

export function mergeResources(target, plus) {
  const mergedResource = {};
  Object.keys(target).forEach((key) => {
    mergedResource[key] = {
      translation: { ...(target[key]?.translation || {}), ...(plus[key]?.translation || {}) }
    };
  });
  return mergedResource;
}
